import { useState } from "react";
import {
  blockers,
  STAGES,
  tbSummary,
  type AuditFile,
  type Procedure,
} from "./india";

type Destination =
  | "Trial balance"
  | "Risks"
  | "Working papers"
  | "Completion"
  | "Findings"
  | "Query assistance";
type Props = {
  files: AuditFile[];
  mode: "analytics" | "quality";
  onOpen: (id: string, tab: Destination) => void;
};
const number = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const samePerson = (a?: string, b?: string) =>
  Boolean(
    a?.trim() && b?.trim() && a.trim().toLowerCase() === b.trim().toLowerCase(),
  );
const independent = (p: Procedure) =>
  p.status === "reviewed" &&
  Boolean(p.reviewedBy?.trim() && p.preparedBy?.trim()) &&
  !samePerson(p.reviewedBy, p.preparedBy);
const engagementLabel = (f: AuditFile) => `${f.clientName} · ${f.periodEnd}`;

function Metric({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <article className="afb-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

export function PortfolioInsights({ files, mode, onOpen }: Props) {
  const [search, setSearch] = useState("");
  const [engagement, setEngagement] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const term = search.trim().toLocaleLowerCase();
  const selectedFiles = files.filter(
    (f) =>
      (!engagement || f.id === engagement) &&
      (includeArchived || f.stage !== "Archived"),
  );
  const qualityFiles = selectedFiles.filter((f) =>
    `${f.clientName} ${f.clientId} ${f.periodEnd} ${f.partner}`
      .toLocaleLowerCase()
      .includes(term),
  );
  const groups = selectedFiles.flatMap((file) => {
    const byMapping = new Map<string, typeof file.tb>();
    for (const row of file.tb) {
      if (
        term &&
        !`${file.clientName} ${file.clientId} ${file.periodEnd} ${row.name} ${row.code} ${row.mapping || "unmapped"}`
          .toLocaleLowerCase()
          .includes(term)
      )
        continue;
      const mapping = row.mapping.trim();
      const mappedRows = byMapping.get(mapping);
      if (mappedRows) mappedRows.push(row);
      else byMapping.set(mapping, [row]);
    }
    return Array.from(byMapping, ([mapping, rows]) => ({
      file,
      mapping,
      rows,
      current: rows.reduce((sum, row) => sum + row.current, 0),
      prior: rows.reduce((sum, row) => sum + row.prior, 0),
    }));
  });
  const matchingIds = new Set(groups.map((group) => group.file.id));
  const analyticFiles = selectedFiles.filter((f) => matchingIds.has(f.id));
  const accountCount = groups.reduce((sum, g) => sum + g.rows.length, 0);
  const unmapped = groups
    .filter((g) => !g.mapping)
    .reduce((sum, g) => sum + g.rows.length, 0);
  const missingTb = selectedFiles.filter(
    (f) =>
      !f.tb.length &&
      `${f.clientName} ${f.clientId} ${f.periodEnd}`
        .toLocaleLowerCase()
        .includes(term),
  );
  const procedures = qualityFiles.flatMap((f) => f.procedures);
  const prepared = procedures.filter((p) => p.status === "prepared").length;
  const reviewed = procedures.filter(independent).length;
  const open = procedures.filter((p) => p.status === "open").length;
  const reviewQueue = qualityFiles
    .map((file) => ({
      file,
      pending: file.procedures.filter(
        (p) =>
          p.status === "prepared" ||
          (p.status === "reviewed" && !independent(p)),
      ),
    }))
    .filter((item) => item.pending.length > 0);
  const riskQueue = qualityFiles.flatMap((file) =>
    file.risks
      .filter((risk) => {
        if (!["High", "Significant"].includes(risk.level)) return false;
        const linked = file.procedures.filter(
          (p) => p.riskIds.includes(risk.id) && !p.naReason.trim(),
        );
        return (
          !risk.response.trim() ||
          !linked.length ||
          linked.some((p) => !independent(p))
        );
      })
      .map((risk) => ({ file, risk })),
  );
  const findingQueue = qualityFiles.flatMap((file) =>
    file.findings
      .filter((finding) => finding.disposition === "open")
      .map((finding) => ({ file, finding })),
  );

  return (
    <section
      className="afb-insights"
      aria-label={mode === "analytics" ? "Audit analytics" : "Quality overview"}
    >
      <header className="afb-section-heading">
        <div>
          <p className="afb-eyebrow">
            {mode === "analytics"
              ? "Understand your balances"
              : "Keep the review moving"}
          </p>
          <h2>
            {mode === "analytics" ? "Data analytics" : "Quality & review"}
          </h2>
          <p className="afb-muted">
            {mode === "analytics"
              ? "Explore comparative balances, account mapping and data readiness across your engagements."
              : "See work awaiting review, unresolved matters and the checks holding up each engagement."}
          </p>
        </div>
      </header>
      <div className="afb-toolbar">
        <label className="afb-field">
          {mode === "analytics"
            ? "Search clients or accounts"
            : "Search engagements"}
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={
              mode === "analytics"
                ? "Client, account, code or mapping"
                : "Client, period or partner"
            }
          />
        </label>
        <label className="afb-field">
          Engagement
          <select
            value={engagement}
            onChange={(event) => setEngagement(event.target.value)}
          >
            <option value="">All engagements</option>
            {files.map((f) => (
              <option key={f.id} value={f.id}>
                {engagementLabel(f)}
                {f.stage === "Archived" ? " · Archived" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="afb-checkbox">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Include archived
        </label>
      </div>
      {files.length === 0 ? (
        <div className="afb-empty">
          <h3>Your engagements will appear here</h3>
          <p>
            Create an engagement from the dashboard to start building your audit
            overview.
          </p>
        </div>
      ) : mode === "analytics" ? (
        <>
          <div className="afb-metrics">
            <Metric
              label="Engagements with balances"
              value={analyticFiles.length}
              detail="Matching the current filters"
            />
            <Metric
              label="Accounts in view"
              value={accountCount}
              detail="From imported trial balances"
            />
            <Metric
              label="Accounts mapped"
              value={
                accountCount
                  ? `${Math.round(((accountCount - unmapped) / accountCount) * 100)}%`
                  : "—"
              }
              detail={`${unmapped} account${unmapped === 1 ? " needs" : "s need"} mapping`}
            />
            <Metric
              label="Source checks pending"
              value={
                analyticFiles.filter(
                  (f) => !f.tbReconciled || !f.tbSource.trim(),
                ).length
              }
              detail="Reconciliation or source reference missing"
            />
          </div>
          <p className="afb-scope-note">
            Analysis uses trial balances saved in this browser. Amounts are
            signed INR balances (debits positive; credits negative), grouped
            within each engagement. This view does not analyse individual ledger
            transactions.
          </p>
          {missingTb.length > 0 && (
            <section className="afb-panel">
              <div className="afb-panel-heading">
                <h3>Ready for a trial balance</h3>
                <span className="afb-badge">{missingTb.length}</span>
              </div>
              <div className="afb-list">
                {missingTb.map((file) => (
                  <div className="afb-list-item" key={file.id}>
                    <div>
                      <strong>{file.clientName}</strong>
                      <p className="afb-muted">
                        {file.periodEnd} · {file.framework} · No trial balance
                        imported
                      </p>
                    </div>
                    <button
                      className="afb-button"
                      onClick={() => onOpen(file.id, "Trial balance")}
                      aria-label={`${file.stage === "Archived" ? "View" : "Import"} trial balance for ${engagementLabel(file)}`}
                    >
                      {file.stage === "Archived"
                        ? "View trial balance"
                        : "Import trial balance"}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="afb-panel">
            <div className="afb-panel-heading">
              <div>
                <h3>Comparative balance explorer</h3>
                <p className="afb-muted">
                  {term
                    ? "Totals reflect only accounts matching your search."
                    : "Expand a mapping to inspect its underlying accounts."}
                </p>
              </div>
              <span className="afb-badge">{groups.length} groups</span>
            </div>
            {!groups.length ? (
              <div className="afb-empty">
                <h3>No balances to display</h3>
                <p>
                  {selectedFiles.some((f) => f.tb.length)
                    ? "Try a different client, account or mapping search."
                    : "Import a trial balance to compare current and prior periods."}
                </p>
              </div>
            ) : (
              <div className="afb-table-wrap">
                <table className="afb-table">
                  <caption className="afb-sr-only">
                    Comparative balances by engagement and mapping
                  </caption>
                  <thead>
                    <tr>
                      <th>Engagement / mapping</th>
                      <th>Accounts</th>
                      <th className="afb-number">Current</th>
                      <th className="afb-number">Prior</th>
                      <th className="afb-number">Movement</th>
                      <th>Explore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => (
                      <tr key={JSON.stringify([group.file.id, group.mapping])}>
                        <td>
                          <strong>{group.mapping || "Unmapped"}</strong>
                          <small className="afb-muted">
                            {engagementLabel(group.file)}
                          </small>
                        </td>
                        <td>{group.rows.length}</td>
                        <td className="afb-number">
                          {number.format(group.current)}
                        </td>
                        <td className="afb-number">
                          {number.format(group.prior)}
                        </td>
                        <td className="afb-number">
                          {number.format(group.current - group.prior)}
                          <small className="afb-muted">
                            {group.prior === 0
                              ? group.current === 0
                                ? "No movement"
                                : "No prior balance"
                              : `${number.format(((group.current - group.prior) / Math.abs(group.prior)) * 100)}% of absolute prior`}
                          </small>
                        </td>
                        <td>
                          <details className="afb-account-detail">
                            <summary>View accounts</summary>
                            <ul>
                              {group.rows.map((row, i) => (
                                <li key={`${row.code}:${i}`}>
                                  <strong>
                                    {row.code} · {row.name}
                                  </strong>
                                  <span>
                                    Current {number.format(row.current)} · Prior{" "}
                                    {number.format(row.prior)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <button
                              className="afb-button"
                              onClick={() =>
                                onOpen(group.file.id, "Trial balance")
                              }
                              aria-label={`Open trial balance for ${engagementLabel(group.file)}, ${group.mapping || "Unmapped"}`}
                            >
                              Open trial balance
                            </button>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {analyticFiles.length > 0 && (
            <section className="afb-panel">
              <div className="afb-panel-heading">
                <h3>Data readiness</h3>
                <span className="afb-muted">
                  Whole engagement trial balances
                </span>
              </div>
              <div className="afb-list">
                {analyticFiles.map((file) => {
                  const summary = tbSummary(file.tb);
                  const ready =
                    summary.currentDifference === 0 &&
                    summary.priorDifference === 0 &&
                    !summary.unmapped &&
                    file.tbReconciled &&
                    file.tbSource.trim();
                  return (
                    <div className="afb-list-item" key={file.id}>
                      <div>
                        <strong>{engagementLabel(file)}</strong>
                        <p className="afb-muted">
                          Current imbalance:{" "}
                          {number.format(summary.currentDifference)} · Prior
                          imbalance: {number.format(summary.priorDifference)} ·{" "}
                          {summary.unmapped} unmapped
                        </p>
                        <small>
                          {file.tbReconciled && file.tbSource.trim()
                            ? `Source reconciliation recorded: ${file.tbSource}`
                            : "Source reconciliation needs documentation"}
                        </small>
                      </div>
                      <div className="afb-inline-actions">
                        <span
                          className={`afb-badge ${ready ? "afb-badge-success" : "afb-badge-warning"}`}
                        >
                          {ready ? "Checks recorded" : "Needs attention"}
                        </span>
                        <button
                          className="afb-button"
                          onClick={() => onOpen(file.id, "Query assistance")}
                          aria-label={`Review data suggestions for ${engagementLabel(file)}`}
                        >
                          Review suggestions
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      ) : (
        <>
          <div className="afb-metrics">
            <Metric
              label="Procedures open"
              value={open}
              detail="Evidence or conclusions still to prepare"
            />
            <Metric
              label="Awaiting review"
              value={prepared}
              detail="Prepared procedures ready for review"
            />
            <Metric
              label="Independently reviewed"
              value={reviewed}
              detail={`Of ${procedures.length} procedures in view`}
            />
            <Metric
              label="Open findings"
              value={findingQueue.length}
              detail={`${findingQueue.filter(({ finding }) => finding.severity === "High").length} high-severity findings`}
            />
          </div>
          <p className="afb-scope-note">
            This overview covers browser-local workpapers and recorded
            evaluation actors. Shared queries and authenticated evidence reviews
            are managed in Collaborate. These checks support engagement review;
            they are not a firm quality-management assessment.
          </p>
          {!qualityFiles.length ? (
            <div className="afb-empty">
              <h3>No matching engagements</h3>
              <p>Adjust your search or include archived engagements.</p>
            </div>
          ) : (
            <>
              <section className="afb-panel">
                <div className="afb-panel-heading">
                  <div>
                    <h3>Independent review queue</h3>
                    <p className="afb-muted">
                      Prepared work and review records needing a different
                      preparer and reviewer.
                    </p>
                  </div>
                  <span className="afb-badge">
                    {reviewQueue.reduce(
                      (sum, item) => sum + item.pending.length,
                      0,
                    )}{" "}
                    procedures
                  </span>
                </div>
                {!reviewQueue.length ? (
                  <div className="afb-empty">
                    <p>
                      No procedures are currently awaiting independent review.
                    </p>
                  </div>
                ) : (
                  <div className="afb-table-wrap">
                    <table className="afb-table">
                      <thead>
                        <tr>
                          <th>Engagement</th>
                          <th>Prepared</th>
                          <th>Review records to correct</th>
                          <th>Reviewed</th>
                          <th>Next action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviewQueue.map(({ file, pending }) => (
                          <tr key={file.id}>
                            <td>
                              <strong>{file.clientName}</strong>
                              <small className="afb-muted">
                                {file.periodEnd} · {file.stage}
                              </small>
                            </td>
                            <td>
                              {
                                pending.filter((p) => p.status === "prepared")
                                  .length
                              }
                            </td>
                            <td>
                              {
                                pending.filter((p) => p.status === "reviewed")
                                  .length
                              }
                            </td>
                            <td>
                              {file.procedures.filter(independent).length} /{" "}
                              {file.procedures.length}
                              <progress
                                className="afb-progress"
                                aria-label={`Reviewed procedures for ${engagementLabel(file)}`}
                                value={
                                  file.procedures.filter(independent).length
                                }
                                max={Math.max(file.procedures.length, 1)}
                              />
                            </td>
                            <td>
                              <button
                                className="afb-button"
                                onClick={() =>
                                  onOpen(file.id, "Working papers")
                                }
                                aria-label={`Review working papers for ${engagementLabel(file)}`}
                              >
                                {file.stage === "Archived"
                                  ? "View working papers"
                                  : "Review working papers"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
              <div className="afb-two-column">
                <section className="afb-panel">
                  <div className="afb-panel-heading">
                    <h3>High risks needing follow-up</h3>
                    <span className="afb-badge afb-badge-warning">
                      {riskQueue.length}
                    </span>
                  </div>
                  <p className="afb-muted">
                    High or significant risks with an incomplete response, no
                    applicable procedure, or work awaiting independent review.
                  </p>
                  <div className="afb-list">
                    {riskQueue.length ? (
                      riskQueue.map(({ file, risk }) => (
                        <div
                          className="afb-list-item"
                          key={`${file.id}:${risk.id}`}
                        >
                          <div>
                            <strong>{risk.title}</strong>
                            <p className="afb-muted">
                              {engagementLabel(file)} · {risk.level}
                            </p>
                          </div>
                          <button
                            className="afb-button"
                            onClick={() => onOpen(file.id, "Risks")}
                            aria-label={`Open risk ${risk.title} for ${engagementLabel(file)}`}
                          >
                            Open risk
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="afb-empty">
                        No high-risk follow-ups in the current view.
                      </p>
                    )}
                  </div>
                </section>
                <section className="afb-panel">
                  <div className="afb-panel-heading">
                    <h3>Unresolved findings</h3>
                    <span className="afb-badge afb-badge-warning">
                      {findingQueue.length}
                    </span>
                  </div>
                  <div className="afb-list">
                    {findingQueue.length ? (
                      findingQueue.map(({ file, finding }) => (
                        <div
                          className="afb-list-item"
                          key={`${file.id}:${finding.id}`}
                        >
                          <div>
                            <strong>{finding.title}</strong>
                            <p className="afb-muted">
                              {engagementLabel(file)} · {finding.severity}
                            </p>
                            <small>
                              Recorded amount: INR{" "}
                              {number.format(finding.amount)}
                            </small>
                          </div>
                          <button
                            className="afb-button"
                            onClick={() => onOpen(file.id, "Findings")}
                            aria-label={`Review finding ${finding.title} for ${engagementLabel(file)}`}
                          >
                            Review finding
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="afb-empty">
                        No open findings in the current view.
                      </p>
                    )}
                  </div>
                </section>
              </div>
              <section className="afb-panel">
                <div className="afb-panel-heading">
                  <div>
                    <h3>Engagement readiness</h3>
                    <p className="afb-muted">
                      Outstanding checks for the next stage, alongside remaining
                      completion checks.
                    </p>
                  </div>
                </div>
                <div className="afb-list">
                  {qualityFiles.map((file) => {
                    const nextStage = STAGES[STAGES.indexOf(file.stage) + 1];
                    const nextChecks = nextStage
                      ? blockers(file, nextStage)
                      : [];
                    const completionChecks = blockers(file, "Completion");
                    return (
                      <div className="afb-list-item" key={file.id}>
                        <div>
                          <strong>{engagementLabel(file)}</strong>
                          <p className="afb-muted">
                            {file.stage}
                            {nextStage
                              ? ` → ${nextStage}`
                              : " · Read-only archive"}
                          </p>
                          {nextStage ? (
                            <details>
                              <summary>
                                {nextChecks.length
                                  ? `${nextChecks.length} checks before ${nextStage}`
                                  : `No recorded checks outstanding for ${nextStage}`}
                              </summary>
                              {nextChecks.length > 0 && (
                                <ul>
                                  {nextChecks.map((check) => (
                                    <li key={check}>{check}</li>
                                  ))}
                                </ul>
                              )}
                            </details>
                          ) : (
                            <span className="afb-badge">Archived</span>
                          )}
                          <small className="afb-muted">
                            {completionChecks.length} completion checks
                            outstanding
                          </small>
                        </div>
                        <button
                          className="afb-button"
                          onClick={() => onOpen(file.id, "Completion")}
                          aria-label={`Open completion checks for ${engagementLabel(file)}`}
                        >
                          Open engagement
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </>
      )}
    </section>
  );
}
